/* eslint-disable no-prototype-builtins */
/**
 * This module contains the topmost-level API for our internal routing
 * abstraction. This module largely contains the React bindings that
 * sit on top of the router. If you're looking for the router itself,
 * look in `./router.js`.
 *
 * The following code is structured largely around two big pieces of
 * context:
 *
 * - RouterContext - tied to the internal router instance, used for
 *   navigating, preloading assets, subscriptions, &c.
 *
 * - EntryContext - tied to the current "entry" of the router. An
 *   "entry" is comprised of a "route" and "location" property.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  Suspense,
  type AllHTMLAttributes,
} from 'react';
import { useSandboxContext } from '@appfabric/providers';

import { LiveAnnounceProvider } from '@mc/components/LiveAnnounceProvider';

import UnwrappedLegacyButtonLink from '@mc/components/ButtonLink';

import usePerformance from '@mc/appfabric/usePerformance';

import { mcIntlEvents } from '@mc/internationalization/events';

import { lazyLoadedPathnames } from '@mc/internationalization/McIntlContext';
import { isIntuitDomain } from '@mc/window';
import { MC_CONTACT_US } from 'Main/HumanAssistance/LiveExpertHelp/OICU/constants/experiences';
import { INTUIT_ASSIST_CONTACT_US } from 'Main/HumanAssistance/LiveExpertHelp/OICU/constants/entryPoints';
import {
  reloadInitComplete,
  reloadWithParam,
} from '../../Main/ErrorHandler/RouterReload/index';

const RouterContext = createContext<RouterValue | null>(null);
const EntryContext = createContext<Entry | null>(null);
const RouteRenderCallbackContext = createContext(noop);

export function normalizePath(path: string) {
  if (path.charAt(path.length - 1) !== '/') {
    return path + '/';
  }
  return path;
}

type Entry = {
  location: {
    hash: string;
    href: string;
    index: number;
    key: string;
    pathname: string;
    search: string;
  };
  route: {
    component: $TSFixMeFunction | $TSFixMe;
    data?: $TSFixMe;
    path: string;
  };
};

type RouterValue = {
  currentEntry: Entry;
  subscribe: $TSFixMeFunction;
  navigate: $TSFixMe;
  redirect: $TSFixMe;
  hasPath: $TSFixMe;
} | null;

type RouterProps = {
  children: React.ReactNode;
  onRouteRender?: $TSFixMeFunction;
  router: RouterValue | null;
};

export const flagCheck = (flagName: string): boolean => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  return Boolean((window as any)[flagName]).valueOf();
};

function Router({ children, router, onRouteRender = noop }: RouterProps) {
  const [entry, setEntry] = useState(() => {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return router.currentEntry;
  });
  const { cancelProfiler } = usePerformance();

  // Let's use optional chaining to ensure we don't get a runtime error
  //  if 'entry' or 'location' are not set for some reason
  const currentPathName = entry?.location?.pathname;
  const prevPathNameRef = useRef(currentPathName);

  const NAV_API_EVENT_KEY = 'appfabric-navigation-api-event-data';
  const eventDetails = {
    detail: {
      experience: MC_CONTACT_US,
      entryPoint: INTUIT_ASSIST_CONTACT_US,
      intent: '',
    },
  };
  const launchOicuEvent = new CustomEvent('launch_oicu', eventDetails);

  const navigate = router?.navigate;

  useEffect(() => {
    if (navigate && !isIntuitDomain()) {
      const handleAppFabricInstanceCreated = () => {
        window.appFabricInstance.pubsub.subscribe(
          NAV_API_EVENT_KEY,
          // @ts-expect-error TS(2345): Argument of type '({ path }: { path: string; }) => void' is not assignable to parameter of type '() => void'.
          ({ path }: { path: string }) => {
            // The Human Assistance team is intercepting a navigation pubsub event if the path matches a unique path
            // we've defined within the Intuit Assist experience. This implementation is necessary to create the
            // desired user experience given limitations in both the GenOS widget and CUI service
            if (
              flagCheck('ha_enable_intuit_assist_front_end') &&
              path === '/#/intuit-assist-handoff'
            ) {
              window.dispatchEvent(launchOicuEvent);
            } else {
              navigate(path);
            }
          },
        );
      };

      window.addEventListener(
        'appFabricInstanceCreated',
        handleAppFabricInstanceCreated,
      );

      return () => {
        window.removeEventListener(
          'appFabricInstanceCreated',
          handleAppFabricInstanceCreated,
        );
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (prevPathNameRef.current !== currentPathName) {
      // We only want to publish in the event that we're navigating away from one of the lazyLoadedPathnames
      // and not if we are navigating from one of the lazyLoadedPathnames to another
      const previousPath = normalizePath(prevPathNameRef.current);
      const currentPath = normalizePath(currentPathName);
      if (
        lazyLoadedPathnames.indexOf(previousPath) !== -1 &&
        lazyLoadedPathnames.indexOf(currentPath) === -1
      ) {
        mcIntlEvents.fetchTranslations();
      }
      prevPathNameRef.current = currentPathName;
    }
  }, [currentPathName]);

  useEffect(() => {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return router.subscribe((nextEntry: $TSFixMe) => {
      setEntry(nextEntry);
      cancelProfiler();
    });
  }, [router, cancelProfiler]);

  return (
    <RouterContext.Provider value={router}>
      <EntryContext.Provider value={entry}>
        <RouteRenderCallbackContext.Provider value={onRouteRender}>
          {/* @ts-expect-error TS(2322): Type 'ReactNode' is not assignable to type 'string... Remove this comment to see the full error message */}
          <LiveAnnounceProvider>{children}</LiveAnnounceProvider>
        </RouteRenderCallbackContext.Provider>
      </EntryContext.Provider>
    </RouterContext.Provider>
  );
}

type ErrorBoundaryProps = {
  data?: $TSFixMe;
  errorFallback?: $TSFixMeFunction;
  location: {
    index: number;
  };
  onError?: $TSFixMeFunction;
};

type ErrorBoundaryState = $TSFixMe;

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static getDerivedStateFromProps(props: $TSFixMe, state: $TSFixMe) {
    if (props.location.index !== state.index) {
      return {
        index: props.location.index,
        error: null,
      };
    }

    return {
      index: props.location.index,
    };
  }

  static getDerivedStateFromError(error: $TSFixMe) {
    return {
      error,
    };
  }

  state = {
    index: null,
    error: null,
  };

  componentDidCatch(error: $TSFixMe) {
    const { data, onError } = this.props;

    if (onError !== undefined) {
      onError(error);
      // Reload route issue: An Error Occurred
      if (!reloadInitComplete()) {
        reloadWithParam('route-reload', 1);
      }
    }

    // In the event that we've loaded some page data for a route that
    // failed to render, flush the data. It might be stale, or it might
    // be a failed request (e.g. a 403), and keeping it in its current
    // state prevents potential error recovery.
    if (data !== undefined && typeof data.flush === 'function') {
      data.flush();
    }
  }

  render() {
    if (this.state.error !== null) {
      const Fallback = this.props.errorFallback;
      // @ts-expect-error TS(2604): JSX element type 'Fallback' does not have any cons... Remove this comment to see the full error message
      return <Fallback error={this.state.error} />;
    }

    return (this.props as $TSFixMe).children;
  }
}

type RouteResolverProps = {
  component?: $TSFixMeFunction | $TSFixMe;
  data?: $TSFixMe;
  notFoundScreen: $TSFixMeFunction;
  queryString?: string;
  redirect: $TSFixMeFunction;
};

// This component is only used internally, and handles
// interfacing with React's Suspense model. In order for
// React to successfully "suspend" the UI, we need to trigger
// this inside a discrete component, downstream of `Suspense`.
function RouteResolver({
  component,
  data,
  queryString,
  notFoundScreen: NotFound,
  redirect,
}: RouteResolverProps) {
  let Component = component;
  let pageData = {};

  // Suspend on component loading.
  if (typeof component.read === 'function') {
    Component = component.read();
  }

  // And suspend on page data loading.
  if (data !== undefined && typeof data.read === 'function') {
    pageData = data.read(queryString);
  }

  // Destroy the cache in the PageData instance
  // when the component unmounts.
  useEffect(() => {
    return () => {
      if (data !== undefined && typeof data.flush === 'function') {
        data.flush();
      }
    };
  }, [data]);

  // If we've resolved our page data and it's telling us to
  // redirect, let's redirect and not try to render the
  // current route.
  useEffect(() => {
    if (pageData && pageData.hasOwnProperty('__url')) {
      redirect((pageData as $TSFixMe).__url);
    }
  }, [pageData, redirect]);

  if (pageData && pageData.hasOwnProperty('__url')) {
    return null;
  }

  // If we've resolved our page data and it returned a 404,
  // then we fallback to the globally-provided `NotFound` view.
  if (pageData && pageData.hasOwnProperty('__404')) {
    return <NotFound />;
  }

  return <Component {...pageData} />;
}

function DefaultNotFound() {
  return '404';
}

type RouteRendererProps = {
  errorScreen: $TSFixMeFunction;
  fallback: React.ReactNode;
  notFoundScreen: $TSFixMeFunction;
  onError?: $TSFixMeFunction;
};

function RouteRenderer({
  onError,
  fallback,
  errorScreen,
  notFoundScreen: NotFound = DefaultNotFound,
}: RouteRendererProps) {
  // @ts-expect-error TS(2339): Property 'redirect' does not exist on type 'null'.
  const { redirect } = useContext(RouterContext);
  // @ts-expect-error TS(2339): Property 'route' does not exist on type 'null'.
  const { route, location } = useContext(EntryContext);

  return (
    // @ts-expect-error TS(2769): No overload matches this call.
    <ErrorBoundary
      errorFallback={errorScreen}
      onError={onError}
      location={location}
      data={route.data}
    >
      <Suspense fallback={fallback}>
        {route.component !== undefined ? (
          <RouteResolver
            component={route.component}
            data={route.data}
            queryString={location.search}
            redirect={redirect}
            notFoundScreen={NotFound}
          />
        ) : (
          <NotFound />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

type RouteProps = {
  children?: React.ReactNode;
  title: string;
};

// Executes the global onRouteRender provided from an upstream Router.
// @ts-expect-error TS(2339): Property 'exitPath' does not exist on type 'RouteP... Remove this comment to see the full error message
function Route({ children, title, exitPath }: RouteProps) {
  const onRouteRender = useContext(RouteRenderCallbackContext);
  const { endMarkedMeasure } = usePerformance();

  useOverrideBackDestination(exitPath);

  useEffect(() => {
    // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
    onRouteRender(title);
  }, [onRouteRender, title]);

  useEffect(() => {
    endMarkedMeasure();
  }, [endMarkedMeasure, onRouteRender, title]);

  return children;
}

type LinkProps = {
  as?: string | React.ReactElement;
  children: React.ReactNode;
  onClick?: $TSFixMeFunction;
  onMouseEnter?: $TSFixMeFunction;
  target?: '_self' | '_blank' | '_parent' | '_top';
  to: string;
} & AllHTMLAttributes<HTMLAnchorElement>;

const Link = React.forwardRef<$TSFixMe, LinkProps>(function Link(
  {
    as: Component = 'a',
    children,
    to,
    onClick,
    onMouseEnter = () => {},
    target,
    ...rest
  },
  ref,
) {
  const navigate = useNavigate();

  const handleClick = useCallback(
    (e: $TSFixMe) => {
      if (typeof onClick === 'function') {
        onClick(e);
      }

      const hasNonSelfTarget = target !== undefined && target !== '_self';

      // Don't navigate when:
      // * e.preventDefault() has been called
      // * the user is not left-clicking
      // * the click event is "modified" (e.g. cmd + click)
      // * _target is set to anything but "_self"
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.altKey ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        hasNonSelfTarget
      ) {
        return;
      }

      e.preventDefault();
      navigate(to);
    },
    [onClick, target, navigate, to],
  );

  // When the user hovers over a code-split route, load that
  // code preemptively.
  const handleMouseEnter = useCallback(
    (e: $TSFixMe) => {
      if (typeof onMouseEnter === 'function') {
        onMouseEnter(e);
      }
    },
    [onMouseEnter],
  );

  return (
    // @ts-expect-error TS(2604): JSX element type 'Component' does not have any con... Remove this comment to see the full error message
    <Component
      {...rest}
      ref={ref}
      target={target}
      href={to}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      {children}
    </Component>
  );
});

// Let's plan to delete this one the redesign components are further
// along--we can codemod the legacy ButtonLink component away, and
// figure out if we want to add a dependency to the router.
function LegacyButtonLink(props: $TSFixMe) {
  return <Link as={UnwrappedLegacyButtonLink} {...props} />;
}

const nullRoute = {};
function noop() {}
function never() {
  return false;
}

function nativeNavigate(to: $TSFixMe) {
  // using window.top to account for cross origin links in bridges
  // @ts-expect-error TS(2531): Object is possibly 'null'.
  window.top.location.assign(to);
}

function nativeGoBack() {
  window.history.go(-1);
}

// This set of hooks presuppose the existence of an upstream Router
// component. In the short-term, this will not always be the case;
// in migrating to the single-page app, there will be many usages
// of these hooks that break that expectation. When that happens,
// they will fallback to other behavior in order to approximate
// their contracts.
function useLocation() {
  const entry = useContext(EntryContext);
  return entry !== null && entry.location
    ? (entry as $TSFixMe).location
    : window.location;
}

function useSearchParam(name: $TSFixMe, defaultValue?: $TSFixMe) {
  const entry = useContext(EntryContext);

  if (entry === null) {
    return defaultValue;
  }

  if ((entry as $TSFixMe).location.searchParams.hasOwnProperty(name)) {
    return (entry as $TSFixMe).location.searchParams[name];
  }

  return defaultValue;
}

function useNavigate() {
  const router = useContext(RouterContext);
  const sandbox = useSandboxContext();
  if (router === null) {
    return nativeNavigate;
  }
  return isIntuitDomain()
    ? sandbox.navigation.navigate
    : (router as $TSFixMe).navigate;
}

function useRedirect() {
  const router = useContext(RouterContext);
  return router !== null ? (router as $TSFixMe).redirect : nativeNavigate;
}

function useHasPath() {
  const router = useContext(RouterContext);
  return router !== null ? (router as $TSFixMe).hasPath : never;
}

function useConfirmation(fn: $TSFixMe) {
  const router = useContext(RouterContext);

  useEffect(() => {
    if (router !== null) {
      return (router as $TSFixMe).addConfirmation(fn);
    }
  }, [router, fn]);
}

function useRoute() {
  const entry = useContext(EntryContext);
  return entry !== null ? (entry as $TSFixMe).route : nullRoute;
}

function useOverrideBackDestination(path: $TSFixMe) {
  const router = useContext(RouterContext);

  useEffect(() => {
    if (router === null) {
      return;
    }
    return (router as $TSFixMe).overrideBackDestination(path);
  }, [router, path]);
}

function useGoBack() {
  const router = useContext(RouterContext);
  return router !== null ? (router as $TSFixMe).goBack : nativeGoBack;
}

function useRedirectMessage() {
  const router = useContext(RouterContext);
  return router !== null
    ? (router as $TSFixMe).getRedirectMessage()
    : undefined;
}

// This hook does not support usage outside the single-page app!
function useReferrer() {
  const router = useContext(RouterContext);
  return router !== null ? (router as $TSFixMe).getReferrer() : '/';
}

export {
  Router,
  RouteRenderer,
  Route,
  Link,
  LegacyButtonLink,
  useConfirmation,
  useLocation,
  useSearchParam,
  useNavigate,
  useRedirect,
  useRedirectMessage,
  useOverrideBackDestination,
  useGoBack,
  useReferrer,
  useHasPath,
  useRoute,
};

export {
  createPageData,
  createSplitRoute,
  createPageDataFromAutolyseResolver,
} from './resources';

export { default as createRouter } from './router';
